import React from "react";

import { cn } from "src/shadcn/lib/utils";

export type IconLike =
  | React.ReactNode
  | React.ComponentType<{ className?: string }>;

/**
 * Injects an icon size className so shadcn's
 * `[&_svg:not([class*='size-'])]:size-X` guard rules skip it and our
 * variant-aware size wins. Caller-provided classes are preserved via cn().
 *
 * - If `node` is a component reference (e.g. lucide's `Search`), it is
 *   instantiated with the size className.
 * - If `node` is a valid React element, it is cloned with the size className
 *   prepended so tailwind-merge lets the caller's className win.
 * - Anything else (string, number, fragment, null/undefined) is returned as-is.
 */
export function withIconSize(
  node: IconLike,
  sizeClassName: string
): React.ReactNode {
  if (typeof node === "function") {
    const Component = node as React.ComponentType<{ className?: string }>;
    return React.createElement(Component, { className: sizeClassName });
  }

  if (!React.isValidElement(node)) return node;

  const element = node as React.ReactElement<{ className?: string }>;

  return React.cloneElement(element, {
    className: cn(sizeClassName, element.props.className),
  });
}
